import { useState, useEffect, useContext } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { AppContext } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Download, Eye, Trash2, Search, Filter } from 'lucide-react';
import { format } from 'date-fns';
import { useMembershipStatus, MembershipBlockedOverlay } from '../components/MembershipGuard';
import { PageSpinner } from '../components/PageSpinner';
import { Pagination } from '../components/Pagination';

const ITEMS_PER_PAGE = 15;

export default function Firmados() {
  const { API_URL, user } = useContext(AppContext);
  const isAdmin = user?.role === 'admin';
  const { isBlocked } = useMembershipStatus();
  const [firmados, setFirmados] = useState([]);
  const [filteredFirmados, setFilteredFirmados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [showDeleted, setShowDeleted] = useState(false);
  const [deletedConsents, setDeletedConsents] = useState([]);
  const [deletedLoaded, setDeletedLoaded] = useState(false);
  const [loadingDeleted, setLoadingDeleted] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    fetchFirmados();
    // "Eliminados" is an admin-only, opt-in view — fetch it lazily on first toggle
    // instead of on every page visit (it was previously always fetched up front, even
    // for non-admin users who can't even see the toggle).
  }, []);

  useEffect(() => {
    filterData();
    setCurrentPage(1);
  }, [firmados, searchTerm, dateFilter]);

  const fetchFirmados = async () => {
    try {
      const token = localStorage.getItem('konemed_token');
      const response = await axios.get(`${API_URL}/consents/signed`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFirmados(response.data);
      setFilteredFirmados(response.data);
    } catch (error) {
      toast.error('Error al cargar consentimientos');
    } finally {
      setLoading(false);
    }
  };

  const fetchDeleted = async () => {
    setLoadingDeleted(true);
    try {
      const token = localStorage.getItem('konemed_token');
      const response = await axios.get(`${API_URL}/consents/deleted`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDeletedConsents(response.data);
      setDeletedLoaded(true);
    } catch (error) {
      toast.error('Error al cargar consentimientos eliminados');
    } finally {
      setLoadingDeleted(false);
    }
  };

  const handleToggleDeleted = () => {
    const next = !showDeleted;
    setShowDeleted(next);
    setCurrentPage(1);
    if (next && !deletedLoaded) {
      fetchDeleted();
    }
  };

  const filterData = () => {
    let filtered = [...firmados];

    if (searchTerm) {
      filtered = filtered.filter(item => 
        `${item.nombre} ${item.primer_apellido}`.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (dateFilter) {
      filtered = filtered.filter(item => {
        const itemDate = new Date(item.created_at).toISOString().split('T')[0];
        return itemDate === dateFilter;
      });
    }

    setFilteredFirmados(filtered);
  };

  const handleDownload = async (serial, numeroDocumento) => {
    // Open the tab synchronously (on the click itself) so popup blockers don't
    // treat it as an unrequested popup once the async request resolves; the
    // document number travels in the request body, never in the URL.
    const pdfWindow = window.open('', '_blank');
    try {
      const response = await axios.post(
        `${API_URL}/consents/download-pdf/${serial}`,
        { numero_documento: numeroDocumento },
        { responseType: 'blob' }
      );
      const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      if (pdfWindow) pdfWindow.location.href = blobUrl;
    } catch (error) {
      if (pdfWindow) pdfWindow.close();
      toast.error('Error al descargar el PDF');
    }
  };

  const handleDelete = async (serial) => {
    if (!window.confirm('¿Estás seguro de eliminar este consentimiento?')) return;

    try {
      const token = localStorage.getItem('konemed_token');
      await axios.delete(`${API_URL}/consents/signed/${serial}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Consentimiento eliminado');
      fetchFirmados();
      fetchDeleted();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  return (
    <DashboardLayout>
      <div data-testid="firmados-page">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-slate-900 mb-2" data-testid="page-title">
            Consentimientos Firmados
          </h1>
          <p className="text-slate-600">Historial de consentimientos completados</p>
        </div>

        {isBlocked && <MembershipBlockedOverlay mode="banner" />}

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  data-testid="search-input"
                  placeholder="Buscar por nombre de paciente..."
                  className="w-full pl-11 pr-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 bg-slate-50"
                />
              </div>
            </div>
            <div className="md:w-64">
              <input
                type="date"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                data-testid="date-filter"
                className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 bg-slate-50"
              />
            </div>
            {isAdmin && (
            <button
              onClick={handleToggleDeleted}
              data-testid="toggle-deleted-button"
              className={`px-6 py-3 rounded-lg font-semibold transition-all ${
                showDeleted
                  ? 'bg-violet-600 text-white'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              {showDeleted ? 'Ver Activos' : 'Ver Eliminados'}
            </button>
            )}
          </div>
        </div>

        {loading ? (
          <PageSpinner />
        ) : showDeleted ? (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="bg-red-50 border-b border-red-200 px-6 py-3">
              <p className="text-sm text-red-800 font-medium">Consentimientos Eliminados</p>
            </div>
            {loadingDeleted ? (
              <div className="p-12 flex justify-center">
                <PageSpinner />
              </div>
            ) : deletedConsents.length === 0 ? (
              <div className="p-12 text-center" data-testid="no-deleted-consents">
                <p className="text-slate-600">No hay consentimientos eliminados</p>
              </div>
            ) : (
              <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Paciente</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Serial</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Eliminado Por</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Fecha Eliminación</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {deletedConsents.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE).map((item) => (
                      <tr key={item.serial_unico}>
                        <td className="px-6 py-4 text-sm text-slate-900">
                          {item.nombre} {item.primer_apellido}
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-600 font-mono">
                          {item.serial_unico}
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-600">
                          {item.deleted_by_name}
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-600">
                          {item.deleted_at ? format(new Date(item.deleted_at), 'dd/MM/yyyy HH:mm') : 'N/A'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="px-6 py-4">
                <Pagination currentPage={currentPage} totalPages={Math.ceil(deletedConsents.length / ITEMS_PER_PAGE)} onPageChange={setCurrentPage} />
              </div>
              </>
            )}
          </div>
        ) : filteredFirmados.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center" data-testid="empty-state">
            <h3 className="text-xl font-bold text-slate-900 mb-2">No hay consentimientos firmados</h3>
            <p className="text-slate-600">Los consentimientos completados aparecerán aquí</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="firmados-table">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Procedimiento</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Paciente</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Documento</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Fecha Creación</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Fecha Firma</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Serial</th>
                    <th className="px-6 py-4 text-right text-sm font-semibold text-slate-900">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredFirmados.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE).map((item) => (
                    <tr key={item.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-6 py-4 text-sm text-slate-900 font-medium">
                        {item.nombre_procedimiento}
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-900">
                        {item.nombre} {item.primer_apellido}
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600 font-mono">
                        {item.numero_documento}
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600">
                        {item.created_at ? format(new Date(item.created_at), 'dd/MM/yyyy') : 'N/A'}
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600">
                        {item.firma_timestamp ? format(new Date(item.firma_timestamp), 'dd/MM/yyyy HH:mm') : 'N/A'}
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600 font-mono">
                        {item.serial_unico}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleDownload(item.serial_unico, item.numero_documento)}
                            data-testid={`download-button-${item.serial_unico}`}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Descargar"
                          >
                            <Download size={18} />
                          </button>
                          {isAdmin && (
                          <button
                            onClick={() => handleDelete(item.serial_unico)}
                            data-testid={`delete-button-${item.serial_unico}`}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Eliminar"
                          >
                            <Trash2 size={18} />
                          </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-6 py-4">
              <Pagination currentPage={currentPage} totalPages={Math.ceil(filteredFirmados.length / ITEMS_PER_PAGE)} onPageChange={setCurrentPage} />
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
